import { assertNever } from '@common/neo.tax';

import {
  ExcelParseError,
  ExcelParseErrorType,
  TabularDataErrorCode,
  TabularDataParseError,
  TabularDataSource,
} from '../graphql';
import { makeFileAndUrl, MakeFileAndUrlResult } from './file.utils';

/**
 * Supports escaping of double quotes, commas, and newlines.
 */
export const escapeCsvValue = (value: string): string => {
  let escapedValue = value;

  if (value.includes('"')) {
    escapedValue = escapedValue.replace(/"/g, '""');
  }
  if (/[\n,"]/.test(escapedValue)) {
    escapedValue = `"${escapedValue}"`;
  }

  return escapedValue;
};

/**
 * Converts Excel parse errors into human-readable strings.
 */
export const interpretExcelParseError = (error: ExcelParseError): string => {
  switch (error.type) {
    case ExcelParseErrorType.DisallowedValue:
      return `Row ${error.row}: Value "${error.value}" is NOT allowed for "${error.column}"`;
    case ExcelParseErrorType.DuplicatedValue:
      return `Row ${error.row}: Value "${error.value}" is duplicated for "${error.column}"`;

    case ExcelParseErrorType.InvalidDate:
      return `Row ${error.row}: Unrecognized date format "${error.value}" for column "${error.column}"`;
    case ExcelParseErrorType.InvalidPercentage:
      return `Row ${error.row}: Percentage for column "${error.column}" should be 0% - 100%`;
    case ExcelParseErrorType.InvalidSheetCount:
      return 'Invalid number of sheets in the Excel file.';
    case ExcelParseErrorType.InvalidTotalPercentage:
      return `Row ${error.row}: Sum of projects should be 0% - 100%`;
    case ExcelParseErrorType.InvalidValue:
      return `Row ${error.row}: Invalid value in the ${error.column} column`;

    case ExcelParseErrorType.MissingAtLeastOneColumnHeader:
      return "This template doesn't have the expected columns. Check that the template you're uploading matches the title of the upload button";
    case ExcelParseErrorType.MissingColumn:
      if (error.value) {
        return `Row ${error.row}: Missing the column for ${error.column} "${error.value}"`;
      }
      return `Row ${error.row}: Missing the column for ${error.column}`;
    case ExcelParseErrorType.MissingValue:
      return `Row ${error.row}: Missing the value for ${error.column}`;

    case ExcelParseErrorType.NonNumericValue:
      return `Row ${error.row}: Invalid number "${error.value}" for column "${error.column}"`;

    case ExcelParseErrorType.UnrecognizedColumnHeader:
      return `Unrecognized column "${error.column}". Rename it to match the template or remove it.`;

    case ExcelParseErrorType.WrongTemplate:
      return `It looks like you may be uploading the ${error.value} template!`;
    default:
      return assertNever(error.type);
  }
};

/**
 * Converts CSV parse errors into human-readable strings.
 */
export const interpretTabularDataParseError = (error: TabularDataParseError): string => {
  switch (error.code) {
    case TabularDataErrorCode.DisallowedValue:
      return `Row ${error.row}: Value "${error.value}" is NOT allowed for "${error.column}"`;
    case TabularDataErrorCode.DuplicatedValue:
      return `Row ${error.row}: Value "${error.value}" is duplicated for "${error.column}"`;

    case TabularDataErrorCode.EmptyFile:
      return error.source === TabularDataSource.Other
        ? 'No records were returned for this fiscal year. Check that the connected provider has data in the selected period.'
        : 'Empty CSV file';

    case TabularDataErrorCode.InvalidDate:
      return `Row ${error.row}: Invalid date for "${error.column}" of "${error.value}"`;
    case TabularDataErrorCode.InvalidEmail:
      return `Row ${error.row}: Invalid email for "${error.column}" of "${error.value}"`;
    case TabularDataErrorCode.InvalidPercentage:
      return `Row ${error.row}: Percentage for column "${error.column}" should be 0% - 100%`;
    case TabularDataErrorCode.InvalidTotalPercentage:
      return `Row ${error.row}: Sum of projects should be 0% - 100%`;
    case TabularDataErrorCode.InvalidValue:
      return `Row ${error.row}: Invalid value in the ${error.column} column`;
    case TabularDataErrorCode.InvalidSheetCount:
      return 'Invalid number of sheets in the Excel file.';

    case TabularDataErrorCode.MissingAtLeastOneColumnHeader:
      return `At least one column header should be present in the following groups: ${error.value}`;
    case TabularDataErrorCode.MissingColumn:
      if (error.value) {
        return `Row ${error.row}: Missing the column for ${error.column} "${error.value}"`;
      }
      return `Row ${error.row}: Missing the column for ${error.column}`;
    case TabularDataErrorCode.MissingColumnHeaders:
      return `Missing column headers: ${error.value}`;
    case TabularDataErrorCode.MissingValue:
      return `Row ${error.row}: Missing "${error.column}"`;

    case TabularDataErrorCode.NonNumericValue:
      return `Row ${error.row}: Invalid number for "${error.column}" of "${error.value}"`;
    case TabularDataErrorCode.NonPositiveNumericValue:
      return `Row ${error.row}: Non-positive number for "${error.column}" of "${error.value}"`;
    case TabularDataErrorCode.NoValidRowsFound:
      return 'Did NOT find any valid rows in the CSV file';

    case TabularDataErrorCode.UnrecognizedColumn: {
      let message = `Unrecognized column: "${error.column}".`;
      if (error.value) {
        message += ` Did you mean "${error.value}"?`;
      }
      return message;
    }
    case TabularDataErrorCode.UnrecognizedValue:
      return `Row ${error.row}: Unrecognized "${error.column}" of "${error.value}"`;

    case TabularDataErrorCode.WrongTemplate:
      return `It looks like you may be uploading the ${error.value} template!`;

    default:
      return assertNever(error.code);
  }
};

/**
 * Syntactic sugar for creating a CSV file and URL via `makeFileAndUrl`.
 */
export const makeCsvFileAndUrl = (csv: string): MakeFileAndUrlResult => makeFileAndUrl(csv, 'text/csv');

/**
 * Intended for lightweight, in-memory CSV string generation without the use of streams. Supports escaping of double quotes, commas, and newlines.
 */
export const stringifyCsv = (data: string[][]): string =>
  data.map((row) => row.map(escapeCsvValue).join(',')).join('\n');
